/**
 * How the aquarium's fish swim (M83, the user's word: "i want to have the
 * aquarium visualizer show the fish moving around. i want their movement to
 * be based on real movement patterns of each species"). Pure arithmetic, so it
 * is tested in node (`tests/fishSwim.test.ts`); `fishSchools.ts` draws it.
 *
 * **The pattern, as measured.** A bullethead keeps to one night shelter on the
 * reef, and at sunrise and at sunset swims between it and a separate daytime
 * feeding ground 500 to 1,800 m away along the reef; it feeds only between
 * sunrise and sunset, and lies sheltered through the night (`Swim`'s sources).
 * So each sprite has its night shelter on its island's reef and its feeding
 * ground along the reef from it, the same way every day; it is drawn making
 * its way out and back over the whole day, at its ground at noon and its
 * shelter at midnight, halfway at sunrise and at sunset (M91, the user's
 * word: an hour's swim is a fifth of a second on the game's clock, and the
 * schools popped between the two; `dayShare`).
 *
 * **The scale, the user's choice.** The box holds 100 km of Maui Nui in 12
 * units: at the opening view a kilometer is a few pixels, and a parrotfish
 * itself far less than one. So each sprite stands for many fish
 * (`FISH_PER_SPRITE`), drawn large enough to see, and the commute is drawn
 * `Swim.wander` times its length (the user's word: "if the realistic movement
 * is imperceptible, make several sprite fish travel across the range of where
 * they live"), its shape and its timing as measured.
 *
 * **Its clock, the user's choice.** The game's day and its hours: a sprite
 * moves as the hours pass, still while the game is paused and quicker at a
 * quicker speed ("pause movement when game is paused and follow the game
 * speed timer"). Since M95 they swim on the player's own seconds instead,
 * moving while the game is paused (main.ts's `swimFish` call; review M116).
 */

/** A species' swimming, each figure with its source as read. */
export interface Swim {
  /** How far its daytime feeding ground lies from its night shelter, m, the nearest and the farthest. */
  commuteM: readonly [number, number];
  /** How many times its commute's length it is drawn. */
  wander: number;
  /** How many of its initial phase's sprites swim in a school (M88); its terminal males patrol round the schools (`schooling.ts`). */
  school: number;
  /**
   * Its own day, where its evidence gives one (M97, the brief's section 6.6: "no common schedule for all"); the
   * parrotfish's, without one, make their way out and back over the whole day (`dayShare`, the user's word, M91).
   */
  day?: DayProfile;
  /**
   * Awake at its night ground as at its day ground, where its evidence has it active at both (M146: the ulua); else
   * resting low and dim at its shelter. Read only without a `day` of its own, whose `awake` rules (M150's review).
   */
  awakeAtNight?: boolean;
  /** Its home range, m², where measured smaller than a habitat's view (M159: the roi's), so it roams within it (`Roam`). */
  homeRangeM2?: number;
  /**
   * How many body lengths above the reef it cruises, where its own (M169, the Reef's redesign v4, its brief's "larger
   * trevally could occasionally pass through the water column", "juvenile fish could shelter"): `fishSchools.ts`'s
   * `CRUISE_BL` unless said.
   */
  cruiseBL?: number;
  /** Whether it drops to the reef in bites by day (M169: the brief's "Parrotfish should graze along surfaces"); unless said, it does. */
  grazes?: boolean;
  /**
   * Whether it rests in the open, in its school over the reef, not in shelter (M233: the taʻape by day, the reviewer's
   * finding): drawn lit at rest, not dimmed as a fish sheltering; unless said, it shelters.
   */
  restsInOpen?: boolean;
}

/**
 * A species' day as the aquarium draws it (M97): at each hour, how far along its commute toward its day ground it
 * is (0 its night shelter, 1 its day ground) and how awake (0 resting at its shelter, low and dim; 1 active), each
 * 0 to 1; drawn moving toward them over at least `easeS` of the player's seconds for a whole change, so a change
 * the game's clock makes in a moment is seen as a swim, not a jump (the brief's section 6.4: "a gentle transition";
 * the user's word, M91: "they pop in and out when it goes between day and night").
 */
export interface DayProfile {
  at(hour: number, dayLengthHours: number): { out: number; awake: number };
  easeS: number;
}

/**
 * How far into a window of the day an hour is, 0 before it to 1 after, eased (a smooth step): `from` and `to` hours
 * of local solar time, the window wrapping past midnight where it must. Its day turns at local midnight, the night's
 * middle: after the window until then, before it from then (M97's review: turned twelve hours from the window's
 * middle, a sunrise window's day ended in the afternoon, and on a day longer than about eleven hours the kala was
 * drawn home by sunset); a window holding midnight itself turns at noon.
 */
export function windowShare(hour: number, from: number, to: number): number {
  const span = to - from;
  const mod24 = (h: number): number => ((h % 24) + 24) % 24;
  const into = mod24(hour - from);
  const midnight = mod24(-from) || 24;
  const turn = midnight <= span ? mod24(12 - from) : midnight;
  const t = into <= span ? into / span : into < turn ? 1 : 0;
  return t * t * (3 - 2 * t);
}

/** How many fish of a group each sprite stands for (JUDGED, four times the sprites, the user's choice, M88: Maui Nui's 75,000 or so adult bullethead come to 120 sprites). */
export const FISH_PER_SPRITE = 625;

/** Sprites for a group of `fish` on each island, in proportion to each island's share of the species' fish, each island's rounded; one for `perSprite` fish. */
export function spritesByIsland(fish: number, islandShares: readonly number[], perSprite = FISH_PER_SPRITE): number[] {
  const total = islandShares.reduce((a, b) => a + b, 0);
  if (total <= 0) return islandShares.map(() => 0);
  return islandShares.map((s) => Math.round((fish * (s / total)) / perSprite));
}

/**
 * How far it has come on its day's way at `hour` (solar hours since midnight, 0 to 24): 0 at midnight, 1 at
 * noon and 0 again at the next midnight, `(hour / 12) ^ p` before noon and the same backward after, `p` putting
 * sunrise and sunset halfway. A day with no daylight keeps it at 0, one with no night at 1; the days between held
 * to 1 to 16 hours, past which the turn at midnight would come at once (M91's review: from 18 hours its pace at
 * midnight is not 0). Every sanctuary with reef fish has 10.9 to 13.4 hours (Maui Nui, MEASURED).
 */
function dayWay(hour: number, dayLengthHours: number): number {
  if (dayLengthHours <= 0) return 0;
  if (dayLengthHours >= 24) return 1;
  const t = Math.max(0, Math.min(12, hour <= 12 ? hour : 24 - hour));
  const sunrise = 12 - Math.max(1, Math.min(16, dayLengthHours)) / 2;
  return Math.pow(t / 12, Math.log(0.5) / Math.log(sunrise / 12));
}

/**
 * How far out along its commute a school is at `hour`: 0 at its night shelter, 1 at its feeding ground. It makes
 * its way out and back over the whole day (the user's word, M91: "they should slowly make their way back and
 * forth throughout the entire game day, not necessarily at the same speed the whole time"; a day on the game's
 * clock is five seconds, M92, so the measured hour's swim each way takes a fifth of one, and the schools popped
 * between shelter and ground): at its shelter at midnight and its ground at noon, halfway at sunrise and at sunset, as
 * Meyer's tagged fish "crossed the NW MPA boundary at sunrise and sunset" on their way (`fishKinds.ts`), fastest
 * about then and slowing to turn at noon and at midnight.
 */
export function dayShare(hour: number, dayLengthHours: number): number {
  return (1 - Math.cos(Math.PI * dayWay(hour, dayLengthHours))) / 2;
}

/** How much a school is travelling at `hour`, 0 to 1: most about sunrise and sunset, none as it turns at noon and at midnight (`dayShare`'s way, its sine). */
export function dayHurry(hour: number, dayLengthHours: number): number {
  return Math.sin(Math.PI * dayWay(hour, dayLengthHours));
}

/** A hash of three integers to [0, 1): the same walk for the same sprite, every time, with no stream of the simulation's. */
export function hash3(a: number, b: number, c: number): number {
  let h = Math.imul(a ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(b + 0x632be5ab, 0xc2b2ae35) ^ Math.imul(c + 0x27d4eb2f, 0x165667b1);
  h = Math.imul(h ^ (h >>> 15), 0x2c1b3c6d);
  h = Math.imul(h ^ (h >>> 12), 0x297a2d39);
  return ((h ^ (h >>> 15)) >>> 0) / 4294967296;
}

/** Km between two columns' centers. */
export function columnKm(a: number, b: number, cells: number, cellKm: number): number {
  return Math.hypot((a % cells) - (b % cells), Math.floor(a / cells) - Math.floor(b / cells)) * cellKm;
}

/**
 * A commute's way over one island's reef, as columns: from its night shelter
 * `start`, on to one of the eight neighbors that is reef of the same island,
 * chosen by the sprite's hash and never back the way it came while there is
 * another, until `km` is covered or the reef ends. So it follows the reef
 * round its island and never crosses land or open sea. `reefOf` holds each
 * reef column's island.
 */
export function reefPath(cells: number, cellKm: number, reefOf: ReadonlyMap<number, number>, start: number, km: number, seed: number): number[] {
  const island = reefOf.get(start);
  const path = [start];
  const seen = new Set(path);
  let covered = 0;
  for (let step = 1; covered < km && step < 1000; step++) {
    const at = path[path.length - 1] as number;
    const r = Math.floor(at / cells);
    const c = at % cells;
    const around: number[] = [];
    for (let dr = -1; dr <= 1; dr++) {
      for (let dc = -1; dc <= 1; dc++) {
        const rr = r + dr;
        const cc = c + dc;
        if ((dr === 0 && dc === 0) || rr < 0 || cc < 0 || rr >= cells || cc >= cells) continue;
        const col = rr * cells + cc;
        if (reefOf.get(col) === island && !seen.has(col)) around.push(col);
      }
    }
    if (around.length === 0) break;
    const next = around[Math.floor(hash3(seed, step, at) * around.length)] as number;
    covered += columnKm(at, next, cells, cellKm);
    path.push(next);
    seen.add(next);
  }
  return path;
}

/** A commute's way with its legs measured once (M83's review: they were measured again twice a fish a frame), in columns' widths. */
export interface Route {
  path: readonly number[];
  legs: readonly number[];
  total: number;
}

/** A path's route, its legs measured in columns' widths on a floor `cells` columns wide. */
export function routeOf(path: readonly number[], cells: number): Route {
  const legs = path.slice(1).map((col, i) => columnKm(path[i] as number, col, cells, 1));
  return { path, legs, total: legs.reduce((a, b) => a + b, 0) };
}

/**
 * Where along a route a share `share` of its way lies, by distance: the two
 * columns it lies between and how far from the first to the second, 0 to 1.
 */
export function pointOnRoute(route: Route, share: number): { from: number; to: number; along: number } {
  const { path, legs } = route;
  let left = Math.min(1, Math.max(0, share)) * route.total;
  for (let i = 0; i < legs.length; i++) {
    const leg = legs[i] as number;
    if (left <= leg) return { from: path[i] as number, to: path[i + 1] as number, along: leg > 0 ? left / leg : 0 };
    left -= leg;
  }
  const last = path[path.length - 1] as number;
  return { from: last, to: last, along: 0 };
}
